import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/api/_admin-guard";

function serverClient() {
  // Publishable-key client for public reads (RLS applies as anon).
  return import("@supabase/supabase-js").then(({ createClient }) => {
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
    return createClient(process.env["SUPABASE_URL"]!, key, {
      auth: { persistSession: false, autoRefreshToken: false },
      global: {
        fetch: (input: any, init: any) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
            h.delete("Authorization");
          }
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });
  });
}

/* ----------------------------------- public --------------------------------- */

export const listPublishedPresentations = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = await serverClient();
  const { data } = await supabase
    .from("class_presentations")
    .select("id, slug, title, summary, cover_image_url, qualification, course_label, published_at")
    .eq("published", true)
    .order("published_at", { ascending: false })
    .limit(60);
  return { presentations: data ?? [] };
});

export const getPublishedPresentation = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => z.object({ slug: z.string().min(1).max(160) }).parse(i))
  .handler(async ({ data }) => {
    const supabase = await serverClient();
    const { data: row } = await supabase
      .from("class_presentations")
      .select("*")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    return { presentation: row ?? null };
  });

/* -------------------------- engagement (signed in) -------------------------- */

export const getPresentationEngagement = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ presentation_id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const [comments, likes, mine] = await Promise.all([
      supabase
        .from("presentation_comments")
        .select("id, author_name, body, created_at")
        .eq("presentation_id", data.presentation_id)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("presentation_likes")
        .select("user_id", { count: "exact", head: true })
        .eq("presentation_id", data.presentation_id),
      supabase
        .from("presentation_likes")
        .select("user_id")
        .eq("presentation_id", data.presentation_id)
        .eq("user_id", userId)
        .maybeSingle(),
    ]);
    return {
      comments: comments.data ?? [],
      likeCount: likes.count ?? 0,
      liked: !!mine.data,
    };
  });

export const togglePresentationLike = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ presentation_id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: existing } = await supabase
      .from("presentation_likes")
      .select("user_id")
      .eq("presentation_id", data.presentation_id)
      .eq("user_id", userId)
      .maybeSingle();
    if (existing) {
      await supabase
        .from("presentation_likes")
        .delete()
        .eq("presentation_id", data.presentation_id)
        .eq("user_id", userId);
      return { liked: false };
    }
    await supabase
      .from("presentation_likes")
      .insert({ presentation_id: data.presentation_id, user_id: userId });
    return { liked: true };
  });

export const addPresentationComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        presentation_id: z.string().uuid(),
        body: z.string().trim().min(2).max(1500),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: profile } = await supabase
      .from("profiles")
      .select("full_name, email")
      .eq("id", userId)
      .maybeSingle();
    const { error } = await supabase.from("presentation_comments").insert({
      presentation_id: data.presentation_id,
      user_id: userId,
      author_name: profile?.full_name || profile?.email?.split("@")[0] || "Student",
      body: data.body,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/* ----------------------------------- admin ---------------------------------- */

export const adminListPresentations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { data } = await context.supabase
      .from("class_presentations")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);
    return { presentations: data ?? [] };
  });

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 90);
}

export const adminSavePresentation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid().optional(),
        title: z.string().trim().min(3).max(200),
        summary: z.string().trim().max(600).optional().default(""),
        cover_image_url: z.string().trim().max(600).optional().default(""),
        file_url: z.string().trim().max(600).optional().default(""),
        embed_url: z.string().trim().max(600).optional().default(""),
        speaker_notes: z.string().max(8000).optional().default(""),
        qualification: z.string().max(40).optional().default("all"),
        course_label: z.string().max(120).optional().default(""),
        allow_download: z.boolean().optional().default(true),
        published: z.boolean().optional().default(false),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const payload: Record<string, unknown> = {
      ...data,
      slug: slugify(data.title) || `presentation-${Date.now()}`,
      created_by: context.userId,
      published_at: data.published ? new Date().toISOString() : null,
    };
    if (!data.id) delete payload.id;
    const { data: row, error } = await context.supabase
      .from("class_presentations")
      .upsert(payload as any, { onConflict: "id" })
      .select("id, slug")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id, slug: row.slug };
  });

export const adminDeletePresentation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { error } = await context.supabase
      .from("class_presentations")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/* --------------------------- admin assignment mgmt -------------------------- */

export const adminListAssignments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { data } = await context.supabase
      .from("student_assignments")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);
    return { assignments: data ?? [] };
  });

export const adminSaveAssignment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid().optional(),
        title: z.string().trim().min(3).max(200),
        description: z.string().max(4000).optional().default(""),
        qualification: z.string().max(40).optional().default("all"),
        course_label: z.string().max(120).optional().default(""),
        due_at: z.string().datetime().nullable().optional(),
        max_marks: z.number().int().min(1).max(1000).optional().default(100),
        published: z.boolean().optional().default(true),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const payload: Record<string, unknown> = { ...data, created_by: context.userId };
    if (!data.id) delete payload.id;
    const { error } = await context.supabase
      .from("student_assignments")
      .upsert(payload as any, { onConflict: "id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Enrol / override a student's qualification from the admin console. */
export const adminSetStudentQualification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        user_id: z.string().uuid(),
        qualification: z.enum(["higher_certificate", "diploma", "llb"]),
        institution: z.string().max(160).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { error } = await context.supabase.from("student_profiles").upsert(
      {
        user_id: data.user_id,
        qualification: data.qualification,
        ...(data.institution ? { institution: data.institution } : {}),
      },
      { onConflict: "user_id" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });
